/**
 * Questo file definisce lo "slice" Redux per la gestione dell'autenticazione.
 * Uno slice Redux, creato con Redux Toolkit, raggruppa la logica dello stato,
 * i reducer e le azioni relative a una specifica funzionalità dell'app (in questo caso, l'auth).
 *
 * - Definisce lo stato iniziale per l'autenticazione.
 * - Crea "thunk" asincroni per gestire le interazioni con l'API di backend,
 * come login, logout, registrazione e verifica dello stato.
 * - Gestisce gli stati di 'pending', 'fulfilled' e 'rejected' di queste operazioni asincrone
 * per aggiornare lo stato (es. impostare 'loading', 'isAuthenticated', 'user', 'error').
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchClient } from '../api/fetchClient';

// --- Thunk Asincroni ---
// I thunk sono azioni che possono eseguire logica asincrona (es. chiamate API)
// e dispatchare altre azioni.

/**
 * Thunk: checkAuthStatus
 * - Contatta l'endpoint 'auth/status.php' per verificare se l'utente ha una sessione attiva.
 * - Utile al caricamento iniziale dell'app per ripristinare lo stato di login.
 */
export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      // Utilizza il client fetch per la chiamata GET.
      return await fetchClient('auth/status.php');
    } catch (error) {
      // Se la chiamata fallisce, ritorna il messaggio di errore.
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: registerUser
 * - Invia i dati di un nuovo utente all'endpoint 'auth/register.php'.
 * @param {object} userData - Dati dell'utente (nome, email, password).
 */
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      // Invia i dati con una richiesta POST.
      return await fetchClient('auth/register.php', { body: userData });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: loginUser
 * - Invia le credenziali dell'utente a 'auth/login.php'.
 * - Se ha successo, il backend restituisce i dati dell'utente.
 * @param {object} credentials - Email e password dell'utente.
 */
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await fetchClient('auth/login.php', { body: credentials });
      return data.user; // Restituisce solo l'oggetto 'user' per lo stato.
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: logoutUser
 * - Invia una richiesta a 'auth/logout.php' per invalidare la sessione sul server.
 */
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchClient('auth/logout.php', { method: 'POST' });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: saveApiKey
 * - Salva la API key di OpenAI per l'utente nel database.
 * @param {object} apiKeyData - Contiene la chiave API.
 */
export const saveApiKey = createAsyncThunk(
  'auth/saveApiKey',
  async (apiKeyData, { rejectWithValue }) => {
    try {
      return await fetchClient('auth/save_api_key.php', { body: apiKeyData });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: changePassword
 * - Permette all'utente di cambiare la propria password.
 * @param {object} passwordData - Password attuale e nuova.
 */
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      return await fetchClient('auth/change_password.php', { body: passwordData });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// --- Stato Iniziale dello Slice ---
const initialState = {
  user: null,             // Dati dell'utente se loggato.
  isAuthenticated: false, // Flag per sapere se l'utente è loggato.
  role: null,             // Ruolo dell'utente (es. 'admin').
  loading: true,          // Flag per indicare se è in corso un'operazione asincrona (inizia a true per il check iniziale).
  error: null,            // Per memorizzare messaggi di errore dalle API.
};

// --- Creazione dello Slice ---
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Qui si potrebbero definire azioni sincrone, se ce ne fossero.
  },
  // 'extraReducers' gestisce le azioni create da 'createAsyncThunk'.
  extraReducers: (builder) => {
    builder
      // --- Gestione di checkAuthStatus ---
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isAuthenticated = action.payload.isAuthenticated;
        state.user = action.payload.isAuthenticated ? action.payload.user : null;
        state.role = action.payload.isAuthenticated ? action.payload.user.ruolo : null;
        state.loading = false;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        // Se il check fallisce, l'utente non è autenticato.
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.loading = false;
        state.error = action.payload;
      })

      // --- Gestione di registerUser ---
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false; // La registrazione è riuscita, ma non effettua il login automatico.
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Es. "Email già in uso".
      })

      // --- Gestione di loginUser ---
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        // Login riuscito: imposta lo stato come autenticato e salva i dati utente.
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.role = action.payload.ruolo;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Es. "Credenziali non valide".
      })

      // --- Gestione di logoutUser ---
      .addCase(logoutUser.fulfilled, (state) => {
        // Logout riuscito: resetta completamente lo stato di autenticazione.
        state.user = null;
        state.isAuthenticated = false;
        state.role = null;
        state.error = null;
      })

      // --- Gestione di saveApiKey e changePassword ---
      // Per queste azioni, potremmo non aver bisogno di aggiornare lo stato globale
      // in modo significativo, ma gestiamo comunque i casi per completezza.
      .addCase(saveApiKey.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

// Esporta il reducer generato dallo slice, che verrà combinato nello store principale.
export default authSlice.reducer;